import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LogOut, Inbox, CalendarDays, Trash2, MailOpen, Mail, Plus, Pencil, X, Check, ExternalLink,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const EMPTY_AGENDA = { title: "", category: "rutin", when: "", description: "", highlights: "", order: 0 };

function MessagesTab() {
  const qc = useQueryClient();
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["admin-contacts"],
    queryFn: async () => (await api.get("/contact")).data,
    refetchOnMount: "always",
    refetchInterval: 30000,
  });

  const markRead = useMutation({
    mutationFn: ({ id, read }) => api.patch(`/admin/contacts/${id}`, { read }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-contacts"] }),
    onError: (e) => toast.error("Gagal memperbarui", { description: formatApiError(e.response?.data?.detail) }),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/admin/contacts/${id}`),
    onSuccess: () => {
      toast.success("Pesan dihapus");
      qc.invalidateQueries({ queryKey: ["admin-contacts"] });
    },
    onError: (e) => toast.error("Gagal menghapus", { description: formatApiError(e.response?.data?.detail) }),
  });

  const unread = messages.filter((m) => !m.read).length;

  if (isLoading) return <p className="text-sm text-slate-500" data-testid="messages-loading">Memuat pesan…</p>;

  return (
    <div data-testid="messages-tab">
      <div className="mb-6 flex items-center gap-3">
        <p className="font-grotesk text-4xl font-extrabold text-navy" data-testid="messages-total">{messages.length}</p>
        <p className="text-sm text-slate-500">
          total pesan
          {unread > 0 && <span className="ml-2 rounded-full bg-gold px-2.5 py-1 text-xs font-semibold text-navy" data-testid="messages-unread">{unread} belum dibaca</span>}
        </p>
      </div>

      {messages.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-navy/15 p-12 text-center text-slate-500" data-testid="messages-empty">
          Belum ada pesan masuk.
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <article
              key={m.id}
              className={`rounded-3xl border p-6 transition-colors ${m.read ? "border-navy/10 bg-white" : "border-gold bg-gold/[0.06]"}`}
              data-testid={`message-card-${m.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-2xl text-navy">{m.name}</h3>
                    {!m.read && <span className="h-2 w-2 rounded-full bg-gold" />}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {m.contact} · <span className="font-medium text-teal">{m.subject}</span>
                  </p>
                </div>
                <span className="text-xs text-slate-400">{fmtDate(m.created_at)}</span>
              </div>
              <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-700">{m.message}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  onClick={() => markRead.mutate({ id: m.id, read: !m.read })}
                  className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:border-navy hover:bg-navy hover:text-sand-50"
                  data-testid={`message-toggle-read-${m.id}`}
                >
                  {m.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                  {m.read ? "Tandai belum dibaca" : "Tandai sudah dibaca"}
                </button>
                <a
                  href={m.contact.includes("@") ? `mailto:${m.contact}` : `https://wa.me/${m.contact.replace(/[^0-9]/g, "").replace(/^0/, "62")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy transition-colors hover:border-teal hover:bg-teal hover:text-sand-50"
                  data-testid={`message-reply-${m.id}`}
                >
                  Balas <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button
                  onClick={() => window.confirm("Hapus pesan ini?") && remove.mutate(m.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-terracotta/30 px-4 py-2 text-xs font-semibold text-terracotta transition-colors hover:bg-terracotta hover:text-sand-50"
                  data-testid={`message-delete-${m.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Hapus
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function AgendaForm({ initial, onDone }) {
  const qc = useQueryClient();
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(
    initial
      ? { ...initial, highlights: (initial.highlights || []).join("\n") }
      : EMPTY_AGENDA,
  );
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const payload = () => ({
    title: form.title.trim(),
    category: form.category,
    when: form.when.trim(),
    description: form.description.trim(),
    highlights: form.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
    order: Number(form.order) || 0,
  });

  const save = useMutation({
    mutationFn: () => (isEdit ? api.put(`/agenda/${initial.id}`, payload()) : api.post("/agenda", payload())),
    onSuccess: () => {
      toast.success(isEdit ? "Agenda diperbarui" : "Agenda ditambahkan");
      qc.invalidateQueries({ queryKey: ["admin-agenda"] });
      qc.invalidateQueries({ queryKey: ["agenda"] });
      onDone();
    },
    onError: (e) => toast.error("Gagal menyimpan", { description: formatApiError(e.response?.data?.detail) }),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="rounded-3xl border border-navy/15 bg-white p-6"
      data-testid="agenda-form"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="eyebrow text-teal">Judul</span>
          <input required minLength={2} value={form.title} onChange={set("title")} className="field mt-2" data-testid="agenda-title-input" />
        </label>
        <label className="block">
          <span className="eyebrow text-teal">Kategori</span>
          <select value={form.category} onChange={set("category")} className="field mt-2 appearance-none" data-testid="agenda-category-select">
            <option value="rutin">Kegiatan Rutin</option>
            <option value="tahunan">Agenda Tahunan</option>
          </select>
        </label>
        <label className="block">
          <span className="eyebrow text-teal">Urutan</span>
          <input type="number" value={form.order} onChange={set("order")} className="field mt-2" data-testid="agenda-order-input" />
        </label>
        <label className="block sm:col-span-2">
          <span className="eyebrow text-teal">Waktu / Jadwal</span>
          <input value={form.when} onChange={set("when")} placeholder="mis. Setiap Ahad · 06.30 WITA" className="field mt-2" data-testid="agenda-when-input" />
        </label>
        <label className="block sm:col-span-2">
          <span className="eyebrow text-teal">Deskripsi</span>
          <textarea rows={3} value={form.description} onChange={set("description")} className="field mt-2 resize-none" data-testid="agenda-description-input" />
        </label>
        <label className="block sm:col-span-2">
          <span className="eyebrow text-teal">Sorotan acara (satu per baris — untuk agenda tahunan)</span>
          <textarea rows={4} value={form.highlights} onChange={set("highlights")} placeholder={"Pawai obor\nBalap jukung\nBazar UMKM"} className="field mt-2 resize-none" data-testid="agenda-highlights-input" />
        </label>
      </div>
      <div className="mt-6 flex gap-3">
        <button type="submit" disabled={save.isPending} className="btn-primary !px-6 !py-3 disabled:opacity-70" data-testid="agenda-save-button">
          <Check className="h-4 w-4" /> {isEdit ? "Simpan Perubahan" : "Tambah Agenda"}
        </button>
        <button type="button" onClick={onDone} className="btn-ghost !px-6 !py-3" data-testid="agenda-cancel-button">
          <X className="h-4 w-4" /> Batal
        </button>
      </div>
    </form>
  );
}

function AgendaTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {} for new | item for edit
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin-agenda"],
    queryFn: async () => (await api.get("/agenda")).data,
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/agenda/${id}`),
    onSuccess: () => {
      toast.success("Agenda dihapus");
      qc.invalidateQueries({ queryKey: ["admin-agenda"] });
      qc.invalidateQueries({ queryKey: ["agenda"] });
    },
    onError: (e) => toast.error("Gagal menghapus", { description: formatApiError(e.response?.data?.detail) }),
  });

  return (
    <div data-testid="agenda-tab">
      {editing !== null ? (
        <AgendaForm initial={editing.id ? editing : null} onDone={() => setEditing(null)} />
      ) : (
        <button onClick={() => setEditing({})} className="btn-accent mb-6" data-testid="agenda-add-button">
          <Plus className="h-4 w-4" /> Tambah Agenda
        </button>
      )}

      {isLoading ? (
        <p className="mt-6 text-sm text-slate-500">Memuat agenda…</p>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((it) => (
            <article key={it.id} className="rounded-3xl border border-navy/10 bg-white p-6" data-testid={`agenda-card-${it.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span className={`eyebrow ${it.category === "tahunan" ? "text-amber" : "text-teal"}`}>
                    {it.category === "tahunan" ? "Agenda Tahunan" : "Kegiatan Rutin"} · #{it.order}
                  </span>
                  <h3 className="mt-1 font-display text-2xl text-navy">{it.title}</h3>
                  {it.when && <p className="mt-1 text-sm font-medium text-slate-500">{it.when}</p>}
                  {it.description && <p className="mt-2 max-w-2xl text-sm text-slate-600">{it.description}</p>}
                  {it.highlights?.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {it.highlights.map((h) => (
                        <li key={h} className="rounded-full bg-sand-100 px-3 py-1 text-xs text-navy">{h}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(it)} className="grid h-10 w-10 place-items-center rounded-full border border-navy/15 text-navy transition-colors hover:border-navy hover:bg-navy hover:text-sand-50" data-testid={`agenda-edit-${it.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => window.confirm("Hapus agenda ini?") && remove.mutate(it.id)} className="grid h-10 w-10 place-items-center rounded-full border border-terracotta/30 text-terracotta transition-colors hover:bg-terracotta hover:text-sand-50" data-testid={`agenda-delete-${it.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("messages");

  const doLogout = () => {
    logout();
    toast.success("Anda telah keluar");
    navigate("/admin/login", { replace: true });
  };

  const tabs = [
    { key: "messages", label: "Pesan Masuk", icon: Inbox },
    { key: "agenda", label: "Agenda Kegiatan", icon: CalendarDays },
  ];

  return (
    <div className="min-h-screen bg-sand-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 border-b border-navy/10 bg-sand-50/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Logo className="h-11 w-11" />
            <div>
              <p className="eyebrow text-teal">Panel Pengurus DKM</p>
              <p className="font-display text-2xl text-navy">Masjid Pantai Bali</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-500 sm:block" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={doLogout} className="btn-ghost !px-4 !py-2.5" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8 flex gap-2" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${
                tab === t.key ? "bg-navy text-sand-50" : "border border-navy/15 text-navy hover:border-navy"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "messages" ? <MessagesTab /> : <AgendaTab />}
      </main>
    </div>
  );
}
